import { clamp } from "@perceptron/utils/math";

import { MAX_PIXELS_PER_SECOND, MIN_PIXELS_PER_SECOND, TIMELINE_VIEWPORT_RATIO } from "../constants";

/** Zoom-in / zoom-out factor for one − or + key press. */
export const ZOOM_STEP_FACTOR = 1.5;

export type TimelineZoom = "in" | "out" | "fit";

/** Zoom at which the whole duration fills `TIMELINE_VIEWPORT_RATIO` of the viewport; also the zoom-out limit. */
export function fitPixelsPerSecond(viewportWidth: number, durationSeconds: number): number {
  return Math.max(MIN_PIXELS_PER_SECOND, (TIMELINE_VIEWPORT_RATIO * viewportWidth) / durationSeconds);
}

/** Furthest scroll, leaving the end `1 − TIMELINE_VIEWPORT_RATIO` of the viewport short of the right edge. */
export function maxScrollLeft(durationSeconds: number, pixelsPerSecond: number, viewportWidth: number): number {
  return Math.max(0, durationSeconds * pixelsPerSecond - TIMELINE_VIEWPORT_RATIO * viewportWidth);
}

export function clampPixelsPerSecond(pixelsPerSecond: number, viewportWidth: number, durationSeconds: number): number {
  return Math.min(Math.max(pixelsPerSecond, fitPixelsPerSecond(viewportWidth, durationSeconds)), MAX_PIXELS_PER_SECOND);
}

/** Scroll that keeps the time under `anchorX` (viewport px) in place across a zoom change. */
export function anchoredScrollLeft({
  scrollLeft,
  fromPixelsPerSecond,
  toPixelsPerSecond,
  anchorX,
  durationSeconds,
  viewportWidth,
}: {
  scrollLeft: number;
  fromPixelsPerSecond: number;
  toPixelsPerSecond: number;
  anchorX: number;
  durationSeconds: number;
  viewportWidth: number;
}): number {
  const anchorTime = (scrollLeft + anchorX) / fromPixelsPerSecond;
  return clamp(
    anchorTime * toPixelsPerSecond - anchorX,
    0,
    maxScrollLeft(durationSeconds, toPixelsPerSecond, viewportWidth),
  );
}
